/**
 * Collection tracing: which controls the applicant pressed, on which screen,
 * and how long each screen held them — so an operator can replay the journey
 * click by click (`case_event` kind `interaction`, stored in ClickHouse).
 *
 * What is recorded is UI vocabulary ONLY: a control's stable `data-track` name
 * (else its kind — see `controlName`) and the flow state it sat on. Never a
 * field's value, a key press, the clipboard or a button's visible text — text
 * inputs are not tracked at all, and the server masks any number regardless.
 *
 * Batched (every few seconds, or sooner when the batch fills) and flushed on
 * page hide with `keepalive`. Best-effort: tracing never blocks a step.
 */

export interface InteractionIn {
  action: 'click' | 'step';
  /** Client epoch ms; the server re-places it on its own clock. */
  at: number;
  screen: string;
  component?: string;
  /** Where the pressed control sits in the page (`click` only); see `xpathOf`. */
  xpath?: string;
  from_screen?: string;
  elapsed_ms?: number;
}

export interface InteractionBatch {
  sent_at: number;
  interactions: InteractionIn[];
}

/** What counts as a pressable control. Text inputs are deliberately absent. */
const CONTROLS =
  'button, a[href], [role="button"], [role="tab"], [role="option"], [role="radio"], [role="checkbox"], [role="switch"], input[type="checkbox"], input[type="radio"], summary';
const FLUSH_MS = 4000;
/** The server refuses more than 50 per request. */
const MAX_BATCH = 50;

/** A stable control name: lowercase `snake_case`, at most 64 characters. */
const TRACK_NAME = /^[a-z][a-z0-9_]{0,63}$/;

/**
 * A control's name: its `data-track`, else what kind of control it is.
 *
 * Never its visible text or accessible name. Those are in the applicant's
 * language (a Thai run would record `ถ่ายภาพ`, an English one `Take photo`, so
 * one press reads as two controls to an operator), and a button's text can
 * quote what the applicant typed. The console translates these names.
 */
export function controlName(el: Element): string {
  const explicit = el.getAttribute('data-track');
  if (explicit && TRACK_NAME.test(explicit)) return explicit;
  const role = el.getAttribute('role');
  if (role === 'option' || role === 'radio' || el.matches('input[type="radio"]')) return 'option';
  if (role === 'checkbox' || role === 'switch' || el.matches('input[type="checkbox"]')) {
    return 'checkbox';
  }
  if (role === 'tab') return 'tab';
  if (el.matches('a[href], .link-button')) return 'link';
  if (el.matches('.btn--ghost')) return 'secondary_button';
  if (el.matches('.btn')) return 'primary_button';
  return 'button';
}

/**
 * Containers whose children are the applicant's CHOICES: a country list, a
 * date wheel or calendar (inside a dialog), search results, questionnaire
 * options. A child's position there says which one was chosen — the 217th
 * country, the 15th day — so below them the path keeps names, not positions.
 */
const CHOICE_CONTAINERS =
  'ul, ol, table, [role="listbox"], [role="radiogroup"], [role="grid"], [role="group"], [role="dialog"]';
/** The server's ceiling on a stored path. */
const MAX_XPATH = 512;

/**
 * An absolute XPath to `el` (`/html/body/div[2]/main/section/button[1]`) — tag
 * names and sibling positions only, never an id, a class or text. A position
 * is written where same-named siblings exist, except inside a choice container.
 */
export function xpathOf(el: Element): string {
  const steps: string[] = [];
  for (let node: Element | null = el; node; node = node.parentElement) {
    const tag = node.localName.toLowerCase();
    const parent = node.parentElement;
    let step = tag;
    if (parent && !parent.closest(CHOICE_CONTAINERS)) {
      const same = [...parent.children].filter((c) => c.localName === node?.localName);
      if (same.length > 1) step += `[${same.indexOf(node) + 1}]`;
    }
    steps.unshift(step);
  }
  // Too deep: drop whole leading steps and say so with `//` (a relative path).
  let prefix = '/';
  while (steps.length > 1 && steps.join('/').length + 2 > MAX_XPATH) {
    steps.shift();
    prefix = '//';
  }
  return prefix + steps.join('/');
}

export interface InteractionRecorder {
  /** The flow moved to `screen`; records a step change when it differs. */
  setScreen: (screen: string) => void;
  /** Send what is queued now (also done on page hide). */
  flush: () => void;
  dispose: () => void;
}

export function armInteractions(send: (batch: InteractionBatch) => void): InteractionRecorder {
  let queue: InteractionIn[] = [];
  let screen: string | null = null;
  let screenSince = Date.now();
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = (): void => {
    if (timer) clearTimeout(timer);
    timer = null;
    while (queue.length > 0) {
      const interactions = queue.slice(0, MAX_BATCH);
      queue = queue.slice(MAX_BATCH);
      try {
        send({ interactions, sent_at: Date.now() });
      } catch {
        /* never throws into the page */
      }
    }
  };
  const push = (item: InteractionIn): void => {
    queue.push(item);
    if (queue.length >= MAX_BATCH) flush();
    else timer ??= setTimeout(flush, FLUSH_MS);
  };

  const onClick = (event: MouseEvent): void => {
    if (!screen || !(event.target instanceof Element)) return;
    const control = event.target.closest(CONTROLS);
    if (!control) return;
    push({
      action: 'click',
      at: Date.now(),
      component: controlName(control),
      screen,
      xpath: xpathOf(control),
    });
  };
  const onHide = (): void => {
    if (document.visibilityState === 'hidden') flush();
  };
  document.addEventListener('click', onClick, true);
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', flush);

  return {
    dispose: () => {
      flush();
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
    },
    flush,
    setScreen: (next) => {
      if (next === screen) return;
      const now = Date.now();
      push({
        action: 'step',
        at: now,
        screen: next,
        ...(screen ? { elapsed_ms: now - screenSince, from_screen: screen } : {}),
      });
      screen = next;
      screenSince = now;
    },
  };
}
